import { Fragment, ReactElement, useContext, useState } from 'react';
import { DialogContext } from './DialogContext';

export interface GenericDialogProps<T> {
  open: boolean;
  setOpen: (boolean) => void;
  callback?: (inputProps: T) => void;
}

function useDialogForContextHelper<TUnique, UCallback>(
  CompFunc: (props: TUnique & GenericDialogProps<UCallback>) => ReactElement,
  callbackStorageKey: string
) {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogProps, setDialogProps] = useState<TUnique>();
  const [callback, setCallback] = useState<(U) => void>();

  const openDialog = (inputProps: TUnique): Promise<UCallback> => {
    setDialogProps(inputProps);
    setDialogOpen(true);
    const prom = new Promise<UCallback>(resolve => {
      setCallback(() => resolve);
    });
    return prom;
  };

  const component = (
    <Fragment key={callbackStorageKey}>
      {dialogOpen && (
        <CompFunc
          open={dialogOpen}
          setOpen={setDialogOpen}
          callback={callback}
          {...dialogProps}
        ></CompFunc>
      )}
    </Fragment>
  );
  return {
    component,
    metadata: { [callbackStorageKey]: openDialog },
  };
}

export const getDialogHooks = <UniqueProps, CallbackInputType>(
  component: (
    props: UniqueProps & GenericDialogProps<CallbackInputType>
  ) => ReactElement
) => {
  const RANDOM_KEY = 'RANDOM_KEY_' + Math.random();

  const useDialogForContext = () =>
    useDialogForContextHelper<UniqueProps, CallbackInputType>(
      component,
      RANDOM_KEY
    );

  const useDialog = () => {
    const contextMetadata = useContext(DialogContext);
    const openFunc = contextMetadata[RANDOM_KEY] as (
      inputProps: UniqueProps
    ) => Promise<CallbackInputType>;
    return openFunc;
  };

  return {
    useDialogForContext,
    useDialog,
  };
};
